import { cn } from "@/utils/cn";
import type { TResizeAxis } from "../types";

interface ResizeHandlesProps {
  widgetName: string;
  sizeLabel: string;
  isMin: boolean;
  isMax: boolean;
  activeAxis: TResizeAxis | null;
  onPointerDown: (
    event: React.PointerEvent<HTMLElement>,
    axis: TResizeAxis,
  ) => void;
  onStep: (direction: 1 | -1) => void;
}

/**
 * Three grab points, one per axis of movement: the right edge widens, the bottom
 * edge deepens, the corner does both.
 *
 * Splitting them matters because the sizes are presets, not free geometry — a
 * single corner handle has to guess whether a mostly-sideways drag meant wider
 * or taller, and guesses wrong often enough to feel broken. Grabbing the right
 * edge says "wider" unambiguously.
 *
 * The corner also carries the keyboard path: focus it and the arrow keys step
 * along the size scale. Without it, resizing would be pointer-only.
 *
 * Ink and target are separate on all three. The two edges used to *be* their
 * 6px bars — a 6px-wide strip is a fine thing to look at and an unreasonable
 * thing to hit, particularly as it is also the strip a pointer crosses on its
 * way off the card. Each bar now sits inside a 24px band that carries the
 * gesture; the bar itself is unchanged, because a 24px slab of primary down the
 * side of every card is not what anyone wants to see. The corner gets the same
 * treatment through `.widget-target`.
 */
export const ResizeHandles = ({
  widgetName,
  sizeLabel,
  isMin,
  isMax,
  activeAxis,
  onPointerDown,
  onStep,
}: ResizeHandlesProps) => {
  // Invisible until the pointer is on the card, then faint, then solid on the
  // handle itself. Always-on edges would put sixteen coloured bars on an
  // eight-widget canvas; hover-only-on-the-handle would leave them undiscoverable
  // for anyone not already looking for them.
  //
  // The hover step keys off the band (`group/handle`) rather than the bar, so
  // the bar lights up as soon as the pointer is somewhere it can grab, not only
  // once it is on the 6px of ink.
  const barBase =
    "rounded-full bg-primary/0 transition-colors group-hover/card:bg-primary/25 group-hover/handle:bg-primary/80";
  const bandBase =
    "group/handle absolute z-20 flex touch-none items-center justify-center";

  return (
    <>
      {/* Right edge — width only. */}
      <span
        aria-hidden
        onPointerDown={(event) => onPointerDown(event, "x")}
        className={cn(
          bandBase,
          "top-1/2 end-0 h-14 w-6 -translate-y-1/2 cursor-ew-resize",
        )}
      >
        <span
          className={cn(
            barBase,
            "h-10 w-1.5",
            activeAxis === "x" && "bg-primary!",
          )}
        />
      </span>

      {/* Bottom edge — height only. */}
      <span
        aria-hidden
        onPointerDown={(event) => onPointerDown(event, "y")}
        className={cn(
          bandBase,
          "bottom-0 left-1/2 h-6 w-14 -translate-x-1/2 cursor-ns-resize",
        )}
      >
        <span
          className={cn(
            barBase,
            "h-1.5 w-10",
            activeAxis === "y" && "bg-primary!",
          )}
        />
      </span>

      {/* Corner — both axes, and the keyboard entry point. */}
      <button
        type="button"
        aria-label={`Resize ${widgetName}. Currently ${sizeLabel}. Arrow keys change size, or drag.`}
        onPointerDown={(event) => onPointerDown(event, "both")}
        onKeyDown={(event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            if (!isMax) onStep(1);
          }
          if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!isMin) onStep(-1);
          }
        }}
        className={cn(
          "widget-target widget-focus absolute end-1 bottom-1 z-20 grid size-5 cursor-nwse-resize touch-none place-items-center",
          "rounded-full text-muted-foreground transition-colors",
          "hover:bg-primary hover:text-primary-foreground",
          activeAxis === "both" && "bg-primary text-primary-foreground",
        )}
      >
        {/* Two stacked ticks — the conventional corner-grip glyph. */}
        <svg
          width="9"
          height="9"
          viewBox="0 0 9 9"
          aria-hidden
          focusable="false"
        >
          <path
            d="M8 3 3 8M8 6.5 6.5 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </button>
    </>
  );
};
